import { MemoizedSelector, Store } from '@ngrx/store';
import { List, Set } from 'immutable';
import { Observable } from 'rxjs';
import { Highlight } from '../search-data/sub-models/highlight.model';
import {
  IEntity,
  IEntityFilters,
  IError,
  ISearchParams,
} from './entity-data.data';
import {
  getAllLists,
  getDispatchedResponse,
  getEditingEntity,
  getEditingEntityChanged,
  getEditingEntitySaved,
  getEntitiesList,
  getHighlightsList,
  getListError,
  getListFetched,
  getListFilters,
  getListFiltersFetched,
  getListRefreshingFilters,
  getMasterError,
  getOriginalEditingEntity,
  getSearchParams,
  getSelectedEntities,
  IEntityDispatchResponse,
  IEntityState,
  IFilteredLists,
  isSavingEditingEntity,
} from './entity-data.reducer';
import { IEntityStore } from './entity-store.interface';
import {
  distinctUntilChanged,
  filter,
  map,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { serialize } from '../../utils/helpers/model.helper';

// should only be used as a superclass; this class is not injectable
export class EntityDataStore<T extends IEntityState, U extends IEntity>
  implements IEntityStore {
  constructor(
    protected store: Store<any>,
    protected selectFeature: MemoizedSelector<Object, T>
  ) {}

  getAllLists(): Observable<IFilteredLists> {
    return this.store.select(getAllLists<T>(this.selectFeature));
  }

  getSortedList(listFilter: string): Observable<List<U>> {
    return this.store.select(
      getEntitiesList<T>(this.selectFeature, listFilter)
    );
  }

  getHighlights(listFilter: string): Observable<List<Highlight>> {
    return this.store.select(
      getHighlightsList<T>(this.selectFeature, listFilter)
    );
  }

  getSearchParams(listFilter: string): Observable<ISearchParams> {
    return this.store.select(
      getSearchParams<T>(this.selectFeature, listFilter)
    );
  }

  getListFetched(listFilter: string): Observable<boolean> {
    return this.store.select(getListFetched<T>(this.selectFeature, listFilter));
  }

  getListError(listFilter: string): Observable<IError> {
    return this.store.select(getListError<T>(this.selectFeature, listFilter));
  }

  getListFilters(listFilter: string): Observable<IEntityFilters> {
    return this.store.select(getListFilters<T>(this.selectFeature, listFilter));
  }

  getListFiltersFetched(listFilter: string): Observable<boolean> {
    return this.store.select(
      getListFiltersFetched<T>(this.selectFeature, listFilter)
    );
  }

  getListRefreshingFilters(listFilter: string): Observable<boolean> {
    return this.store.select(
      getListRefreshingFilters<T>(this.selectFeature, listFilter)
    );
  }

  getEditingEntity(): Observable<U> {
    return this.store.select(getEditingEntity<T>(this.selectFeature));
  }

  getOriginalEditingEntity(): Observable<U> {
    return this.store.select(getOriginalEditingEntity<T>(this.selectFeature));
  }

  hasEditingEntityChanged(): Observable<boolean> {
    return this.store.select(getEditingEntityChanged<T>(this.selectFeature));
  }

  hasEditingEntityValueChanged(): Observable<boolean> {
    return this.getEditingEntity().pipe(
      filter((x) => !!x),
      withLatestFrom(this.getOriginalEditingEntity()),
      map(([editingEntity, originalEditingEntity]: U[]) => {
        const serializedEditingEntity = serialize(editingEntity);
        const serializedOriginalEditingEntity = serialize(
          originalEditingEntity
        );

        return !serializedEditingEntity.equals(serializedOriginalEditingEntity);
      }),
      distinctUntilChanged()
    );
  }

  hasEditingEntityBeenSaved(): Observable<boolean> {
    return this.store.select(getEditingEntitySaved<T>(this.selectFeature));
  }

  isSavingEditingEntity(): Observable<boolean> {
    return this.store.select(isSavingEditingEntity<T>(this.selectFeature));
  }

  getValidEditingEntity(): Observable<U> {
    return this.getEditingEntity().pipe(
      filter((x) => !!x),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );
  }

  getSelectedEntities(): Observable<Set<U>> {
    return this.store.select(getSelectedEntities<T>(this.selectFeature));
  }

  getDispatchedResponse<V>(dispatchId: string): Observable<V> {
    return this.store.select(getDispatchedResponse(this.selectFeature)).pipe(
      filter(
        (response: IEntityDispatchResponse) =>
          response && response.dispatchId === dispatchId
      ),
      tap((response: IEntityDispatchResponse) => {
        if (!response.isSuccess) {
          throw response.error;
        }
      }),
      map((response: IEntityDispatchResponse) => response.entity as V),
      take(1)
    );
  }

  getMasterError(): Observable<IError> {
    return this.store.select(getMasterError<T>(this.selectFeature));
  }
}
